import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faShareNodes,
  faLink,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import {
  faWhatsapp,
  faFacebook,
  faXTwitter,
} from "@fortawesome/free-brands-svg-icons";
import styles from "./Blog.module.css";
import { BlogCover } from "./BlogCard";
import useDocumentMeta from "./useDocumentMeta";
import {
  blogDateTimeAttr,
  buildShareLinks,
  copyToClipboard,
  formatBlogDate,
  safeCtaUrl,
  sanitizeBlogHtml,
} from "./blogUtils";
import { blogService } from "../../../services/api";
import { EmptyState, Spinner } from "../../../components/ui";

/** Compartir: navigator.share en móvil; si no existe, menú con copiar enlace y redes. */
function ShareButton({ url, title, text }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const rootRef = useRef(null);
  const links = useMemo(() => buildShareLinks(url, title), [url, title]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    setOpen((v) => !v);
  };

  const handleCopy = async () => {
    const ok = await copyToClipboard(url);
    setCopied(ok);
    if (ok) setTimeout(() => setCopied(false), 2200);
  };

  return (
    <div className={styles.share} ref={rootRef}>
      <button
        type="button"
        className={styles.shareBtn}
        onClick={handleShare}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <FontAwesomeIcon icon={faShareNodes} />
        Compartir
      </button>
      {open && (
        <div className={styles.shareMenu} role="menu">
          <button type="button" role="menuitem" className={styles.shareItem} onClick={handleCopy}>
            <FontAwesomeIcon icon={faLink} />
            {copied ? "¡Enlace copiado!" : "Copiar enlace"}
          </button>
          <a
            role="menuitem"
            className={styles.shareItem}
            href={links.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FontAwesomeIcon icon={faWhatsapp} />
            WhatsApp
          </a>
          <a
            role="menuitem"
            className={styles.shareItem}
            href={links.facebook}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FontAwesomeIcon icon={faFacebook} />
            Facebook
          </a>
          <a
            role="menuitem"
            className={styles.shareItem}
            href={links.x}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FontAwesomeIcon icon={faXTwitter} />X
          </a>
        </div>
      )}
      <span className={styles.srOnly} aria-live="polite">
        {copied ? "Enlace copiado" : ""}
      </span>
    </div>
  );
}

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setPost(null);
    blogService.get(slug).then((res) => {
      if (cancelled) return;
      setPost(res.ok ? res.data : null);
      setStatus(res.status);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const bodyHtml = useMemo(() => sanitizeBlogHtml(post?.body_html), [post?.body_html]);
  const ctaUrl = safeCtaUrl(post?.cta_url);
  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/blog/${encodeURIComponent(slug || "")}`
      : "";
  const date = formatBlogDate(post?.published_at);

  useDocumentMeta(
    post ? `${post.seo_title || post.title} | Gold Loterías` : "",
    post?.seo_description || post?.excerpt || ""
  );

  if (loading) {
    return (
      <div className={styles.page}>
        <Spinner label="Cargando artículo…" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className={styles.page}>
        <EmptyState
          title={status === 404 ? "No encontramos este artículo" : "Pronto publicaremos nuevos artículos"}
          description={
            status === 404
              ? "Puede que haya sido retirado o que el enlace esté incompleto."
              : "No pudimos cargar el artículo en este momento."
          }
        />
        <div className={styles.centerRow}>
          <Link to="/blog" className={styles.backLink}>
            <FontAwesomeIcon icon={faArrowLeft} /> Volver al blog
          </Link>
        </div>
      </div>
    );
  }

  const CtaTag = ctaUrl.startsWith("/") ? Link : "a";
  const ctaProps = ctaUrl.startsWith("/")
    ? { to: ctaUrl }
    : { href: ctaUrl, target: "_blank", rel: "noopener noreferrer" };

  return (
    <article className={`${styles.page} ${styles.article}`}>
      <Link to="/blog" className={styles.backLink}>
        <FontAwesomeIcon icon={faArrowLeft} /> Volver al blog
      </Link>

      <header className={styles.articleHeader}>
        <div className={styles.cardMeta}>
          {post.category && <span className={styles.category}>{post.category}</span>}
          {date && (
            <time dateTime={blogDateTimeAttr(post.published_at)} className={styles.date}>
              {date}
            </time>
          )}
          {Number(post.read_minutes) > 0 && (
            <span className={styles.date}>
              <FontAwesomeIcon icon={faClock} /> {post.read_minutes} min de lectura
            </span>
          )}
        </div>
        <h1 className={styles.articleTitle}>{post.title}</h1>
        {post.subtitle && <p className={styles.articleSubtitle}>{post.subtitle}</p>}
        <ShareButton url={shareUrl} title={post.title} text={post.excerpt || ""} />
      </header>

      {(post.cover_image || post.cover_image_mobile) && (
        <BlogCover post={post} className={styles.articleCover} eager />
      )}

      <div
        className={styles.articleBody}
        // body_html viene sanitizado del backend y se limpia de nuevo en sanitizeBlogHtml.
        dangerouslySetInnerHTML={{ __html: bodyHtml }}
      />

      {ctaUrl && (
        <div className={styles.ctaBox}>
          <CtaTag className={styles.ctaBtn} {...ctaProps}>
            {post.cta_text || "Jugar ahora"}
            <FontAwesomeIcon icon={faArrowRight} />
          </CtaTag>
        </div>
      )}

      <footer className={styles.articleFooter}>
        <Link to="/blog" className={styles.backLink}>
          <FontAwesomeIcon icon={faArrowLeft} /> Volver al blog
        </Link>
        <ShareButton url={shareUrl} title={post.title} text={post.excerpt || ""} />
      </footer>
    </article>
  );
};

export default BlogPostPage;
